import React, { useCallback, useMemo } from 'react'

import {
  ResponsiveCirclePacking,
  ComputedDatum,
  CirclePackingCommonProps,
  ComputedLabel,
} from '@nivo/circle-packing'
import { Card, Skeleton } from 'antd'
import { T } from 'govwell-ui'

import EmptyState from 'src/components/EmptyState'
import { TailwindColor } from 'src/constants/theme/tailwind'
import Flex from 'src/govwell-ui/components/Flex/Flex'

import ChartTooltip from '../ChartTooltip'
import DashboardLabel from '../DashboardLabel'

export type Node = {
  id: string
  label: string
  value: number
}

type CirclePackingDataNode = Node & {
  children?: CirclePackingDataNode[]
}

const ChildColor: CirclePackingCommonProps<Node>['childColor'] = {
  from: 'color',
  modifiers: [['brighter', 0.4]],
}

type Props = {
  error?: Error | null
  isLoading: boolean
  label?: React.ReactNode
  subLabel?: React.ReactNode
  labelTooltip?: string
  nodes: Node[]
  emptyMessage?: string
  onClick?: (nodeId: string) => void
  onHover?: (nodeId: string | null) => void
}

// Blue color palette matching the reference design
const BLUE_COLORS = [
  TailwindColor.Sky400,
  TailwindColor.Sky300,
  TailwindColor.Sky200,
  TailwindColor.Sky100,
  TailwindColor.Sky50,
]

const Theme = {
  labels: {
    text: {
      fontSize: 14,
      fontWeight: 700,
    },
  },
}

const CirclePackingChart = (props: Props) => {
  const { isLoading, label, subLabel, labelTooltip, nodes, emptyMessage, onClick, onHover } = props

  // Build the data structure required by Nivo circle packing
  const data: CirclePackingDataNode = useMemo(
    () => ({
      id: 'root',
      label: 'Nodes',
      value: 0,
      children: nodes.map((node) => ({
        id: node.id,
        label: node.label,
        value: node.value,
      })),
    }),
    [nodes]
  )

  // Assign colors based on hash of id for consistency
  const getColor = useCallback(
    (node: Omit<ComputedDatum<CirclePackingDataNode>, 'color' | 'fill'>): string => {
      if (node.depth === 0) {
        return 'transparent'
      }
      // Hash the id to get a consistent color index
      const hashCode = String(node.id)
        .split('')
        .reduce((acc, char) => {
          return char.charCodeAt(0) + ((acc << 5) - acc)
        }, 0)
      const index = Math.abs(hashCode) % BLUE_COLORS.length
      return BLUE_COLORS[index] ?? BLUE_COLORS[0]!
    },
    []
  )

  const handleMouseEnter = useCallback(
    (node: ComputedDatum<CirclePackingDataNode>) => {
      if (node.depth > 0) {
        onHover?.(String(node.id))
      }
    },
    [onHover]
  )

  const handleMouseLeave = useCallback(() => {
    onHover?.(null)
  }, [onHover])

  const handleClick = useCallback(
    (node: ComputedDatum<CirclePackingDataNode>) => {
      if (node.depth > 0) {
        onClick?.(String(node.id))
      }
    },
    [onClick]
  )

  const renderTooltip = useCallback((node: ComputedDatum<CirclePackingDataNode>) => {
    const tooltip = {
      id: String(node.id),
      value: `${node.data.label}: ${node.value} question${node.value !== 1 ? 's' : ''}`,
    }
    return <ChartTooltip tooltips={[tooltip]} />
  }, [])

  const isEmpty = nodes.length === 0

  const labelsFilter = useCallback((labelData: ComputedLabel<CirclePackingDataNode>) => {
    return labelData.node.depth > 0
  }, [])

  const labelFn = useCallback((node: ComputedDatum<CirclePackingDataNode>) => {
    const nodeLabel = node.data.label
    const maxLen = Math.max(3, Math.floor(node.radius / 5))
    if (nodeLabel.length > maxLen) {
      return nodeLabel.substring(0, maxLen - 2).toUpperCase() + '...'
    }
    return nodeLabel.toUpperCase()
  }, [])

  return (
    <Card className="w-full">
      <Flex direction="column" gap={4}>
        <DashboardLabel label={label} tooltip={labelTooltip} isBold />
        {subLabel && <T className="w-full text-sm text-gray-500">{subLabel}</T>}
        <div className="h-[400px] w-full">
          {isEmpty && (
            <EmptyState hideBorder>
              <EmptyState.Image />
              <EmptyState.Message>{emptyMessage}</EmptyState.Message>
            </EmptyState>
          )}
          {isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
          <ResponsiveCirclePacking
            data={data}
            id="id"
            value="value"
            colors={getColor}
            childColor={ChildColor}
            padding={4}
            enableLabels
            labelsFilter={labelsFilter}
            labelsSkipRadius={20}
            labelTextColor={TailwindColor.Sky950}
            label={labelFn}
            theme={Theme}
            borderWidth={0}
            tooltip={renderTooltip}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
            animate
            motionConfig="gentle"
            leavesOnly
          />
        </div>
      </Flex>
    </Card>
  )
}

export default React.memo(CirclePackingChart)
